import React, { useState } from 'react'

import {
  Action,
  AppointmentField,
  CustomField,
  DateField,
  DateTimeField,
  FlagsField,
  FieldSpec,
  LinkedField,
  PaginatedTable,
  RelatedLinkedField,
  ValueField,
  StatusField,
} from 'src/components/Table'
import { FreightOrder, FreightOrderStatusEnum, nextOrderStatus } from 'src/api/models'
import { OrderLabel } from 'src/modules/supply_chain/components/freight/OrderLabel'
import { FreightOrderDialog } from 'src/modules/supply_chain/components/freight/OrderDialog'
import { PickTicket } from 'src/modules/supply_chain/components/freight/PickTicket'
import { BulkPrint } from 'src/components/Print/Bulk'
import {
  BooleanFilter,
  createCustomFieldFilter,
  createEnumFilter,
  createMultiQuerySelectFilter,
  DateFilter,
  DateTimeFilter,
  FilterHeader,
  MultStringFilter,
} from 'src/modules/shared/components/filters'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { skuit } from 'src/services/skuit'
import { ListTableProps } from './types'

const fieldOptions: { [key: string]: FieldSpec } = {
  number: { id: 'number', label: 'Number', renderer: LinkedField(ValueField) },
  client: { id: 'client.name', label: 'Client', ordering: 'account__name', renderer: ValueField },
  placedOn: {
    id: 'placedOn',
    label: 'Date Placed',
    ordering: 'placed_on',
    renderer: DateTimeField,
  },
  processOn: {
    id: 'processOn',
    label: 'Date to Process On',
    ordering: 'process_on',
    renderer: DateField,
  },
  fulfillOn: {
    id: 'fulfillOn',
    label: 'Date to Fulfill On',
    ordering: 'fulfill_on',
    renderer: DateTimeField,
  },
  cannotShipBefore: {
    id: 'cannotShipBefore',
    label: 'Ship On or After',
    ordering: 'cannot_ship_before',
    renderer: DateField,
  },
  mustShipBy: {
    id: 'mustShipBy',
    label: 'Must Ship By',
    ordering: 'must_ship_by',
    renderer: DateField,
  },
  expectedShipDate: {
    id: 'expectedShipDate',
    label: 'Expected Ship Date',
    ordering: 'expected_ship_date',
    renderer: DateField,
  },
  mustCancelBy: {
    id: 'mustCancelBy',
    label: 'Must Cancel By',
    ordering: 'must_cancel_by',
    renderer: DateField,
  },
  consignee: { id: 'address.consignee', label: 'Consignee', renderer: ValueField },
  status: { id: 'status', label: 'Status', renderer: StatusField },
  flags: { id: 'flags', label: 'Flags', renderer: FlagsField },
  customFields: { id: 'customFields', label: 'Custom Fields', renderer: CustomField },
  retailer: {
    id: 'retailer',
    label: 'Retailer',
    ordering: 'retailer__name',
    renderer: RelatedLinkedField(ValueField, 'name'),
  },
  nextAppointment: {
    id: 'nextAppointment',
    label: 'Next Appointment',
    ordering: 'next_appointment__start',
    renderer: AppointmentField,
  },
}

export const FreightOrderListTable = (props: ListTableProps) => {
  const { paginator, fields, bulkActions, title = 'Freight Orders', extraActions } = props
  const [selected, setSelected] = useState<FreightOrder[]>([])
  const { print, renderPrintComponent } = useAsyncPrint()
  const [bulkEditOpen, setBulkEditOpen] = useState(false)

  const allOrdersHaveSameStatus = selected.every((order) => order.status === selected[0]?.status)

  const bulkOptions: { [key: string]: Action } = {
    printOrder: {
      label: 'Print Orders',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              onReadyToPrint?.()
              return <OrderLabel order={obj} />
            }}
          />
        ))
      },
    },
    printPickTicket: {
      label: 'Print Pick-Tickets',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              return <PickTicket onReadyToPrint={onReadyToPrint} order={obj} />
            }}
          />
        ))
      },
    },
    printAll: {
      label: 'Print Orders & Pick Tickets',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              return (
                <>
                  <OrderLabel order={obj} />
                  <PickTicket onReadyToPrint={onReadyToPrint} order={obj} />
                </>
              )
            }}
          />
        ))
      },
    },
    printPlacard: {
      label: 'Print Placards',
      action: () => {
        print((props) => (
          <BulkPrint
            {...props}
            items={selected}
            render={({ obj, onReadyToPrint }) => {
              onReadyToPrint?.()
              return <OrderLabel short order={obj} />
            }}
          />
        ))
      },
    },
    divider: {
      label: 'divider',
      action: () => {
        // this is a divider for visual organization purposes
      },
    },
    status: {
      label:
        selected.length > 0 && selected[0].status && allOrdersHaveSameStatus
          ? `Set order${selected.length === 1 ? '' : 's'} as ${nextOrderStatus(selected[0].status)}`
          : "Can't update status",
      action: async (orders: FreightOrder[]) => {
        if (orders[0].status) {
          const newStatus = nextOrderStatus(orders[0].status)
          await Promise.all(
            orders.map((order) => {
              if (order.guid) {
                order.status = newStatus
                return skuit.sc.freight.orders.patch(order.guid, { status: newStatus })
              }
              return new Promise(() => {})
            })
          )
          paginator.refresh()
        }
      },
      isEnabled: allOrdersHaveSameStatus,
    },
    cancel: {
      label: `Cancel order${selected.length === 1 ? '' : 's'}`,
      action: async (orders: FreightOrder[]) => {
        await Promise.all(
          orders.map((order) => {
            if (order.guid) {
              return skuit.sc.freight.orders.patch(order.guid, {
                status: FreightOrderStatusEnum.canceled,
              })
            }
            return new Promise(() => {})
          })
        )
        paginator.refresh()
      },
    },
    bulkEdit: {
      label: 'Bulk Edit',
      action: () => setBulkEditOpen(true),
    },
  }

  return (
    <>
      {renderPrintComponent()}
      {bulkEditOpen && (
        <FreightOrderDialog
          open={bulkEditOpen}
          handleClose={() => setBulkEditOpen(false)}
          bulkUpdate
          orders={selected}
        />
      )}
      <PaginatedTable
        title={title}
        fields={fields.map((field) => fieldOptions[field])}
        paginator={paginator}
        onSelectedItem={setSelected}
        actions={extraActions}
        bulkActions={bulkActions?.map((action) => bulkOptions[action])}
        filters={[
          {
            id: 'clients',
            label: 'Clients',
            widget: createMultiQuerySelectFilter(skuit.sc.clients.list),
          },
          {
            id: 'retailers',
            label: 'Retailers',
            widget: createMultiQuerySelectFilter(skuit.sc.freight.retailers.list),
          },
          {
            id: 'statuses',
            label: 'Status',
            widget: createEnumFilter(FreightOrder.Status),
          },
          {
            id: 'flags',
            label: 'Flags',
            widget: createMultiQuerySelectFilter(skuit.sc.freight.orders.flags.list),
          },
          {
            id: 'carriers',
            label: 'Carriers',
            widget: createMultiQuerySelectFilter(skuit.sc.carriers.list),
          },
          {
            id: 'numbers',
            label: 'Order Numbers',
            widget: MultStringFilter,
          },
          {
            id: 'custom_fields',
            label: 'Custom Fields',
            widget: createCustomFieldFilter(
              skuit.sc.clients.list,
              skuit.sc.freight.orders.customFields.list
            ),
          },

          {
            id: 'scheduling_header',
            label: 'Scheduling',
            widget: FilterHeader,
          },
          {
            id: 'unscheduled',
            label: 'Unscheduled',
            widget: BooleanFilter,
          },
          {
            id: 'scheduled_on_start',
            label: 'Scheduled On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'scheduled_on_end',
            label: 'Scheduled On or Before',
            widget: DateTimeFilter,
          },

          {
            id: 'placed_on_header',
            label: 'Placed On',
            widget: FilterHeader,
          },
          {
            id: 'placed_on_start',
            label: 'Placed On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'placed_on_end',
            label: 'Placed On or Before',
            widget: DateTimeFilter,
          },

          {
            id: 'date_to_fulfill_on_header',
            label: 'Date to Fulfill',
            widget: FilterHeader,
          },
          {
            id: 'fulfill_on_start',
            label: 'Date to Fulfill On or After',
            widget: DateFilter,
          },
          {
            id: 'fulfill_on_end',
            label: 'Date to Fulfill On or Before',
            widget: DateFilter,
          },

          {
            id: 'cannot_ship_before_header',
            label: 'Ship On or After',
            widget: FilterHeader,
          },
          {
            id: 'cannot_ship_before_start',
            label: 'Ship On or After, On or After',
            widget: DateFilter,
          },
          {
            id: 'cannot_ship_before_end',
            label: 'Ship On or After, On or Before',
            widget: DateFilter,
          },

          {
            id: 'must_ship_by_header',
            label: 'Must Ship By',
            widget: FilterHeader,
          },
          {
            id: 'must_ship_by_start',
            label: 'Must Ship By, On or After',
            widget: DateFilter,
          },
          {
            id: 'must_ship_by_end',
            label: 'Must Ship By, On or Before',
            widget: DateFilter,
          },

          {
            id: 'expected_ship_date_header',
            label: 'Expected Ship Date',
            widget: FilterHeader,
          },
          {
            id: 'expected_ship_date_start',
            label: 'Expected Ship Date, On or After',
            widget: DateFilter,
          },
          {
            id: 'expected_ship_date_end',
            label: 'Expected Ship Date, On or Before',
            widget: DateFilter,
          },

          {
            id: 'must_cancel_by_header',
            label: 'Must Cancel By',
            widget: FilterHeader,
          },
          {
            id: 'must_cancel_by_start',
            label: 'Must Cancel By, On or After',
            widget: DateFilter,
          },
          {
            id: 'must_cancel_by_end',
            label: 'Must Cancel By, On or Before',
            widget: DateFilter,
          },

          {
            id: 'created_header',
            label: 'Created',
            widget: FilterHeader,
          },
          {
            id: 'created_on_start',
            label: 'Created On or After',
            widget: DateTimeFilter,
          },
          {
            id: 'created_on_end',
            label: 'Created On or Before',
            widget: DateTimeFilter,
          },
        ]}
      />
    </>
  )
}
